import React, { useState } from 'react';
import { Input } from 'antd';
import { LockOutlined, LoadingOutlined, CheckCircleTwoTone } from '@ant-design/icons';

export interface InternalNotesFieldProps {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  /**
   * Optional persist hook. When provided, the current value is saved on blur.
   * Shows a subtle saving/saved indicator; failures surface via the caller
   * (the caller is expected to message.error). Returning a rejected promise
   * flips the indicator to an error state.
   */
  onSave?: (v: string) => Promise<void> | void;
}

export const InternalNotesField: React.FC<InternalNotesFieldProps> = ({
  value,
  onChange,
  rows = 3,
  onSave,
}) => {
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const handleBlur = async () => {
    if (!onSave) return;
    setSaveState('saving');
    try {
      await onSave(value);
      setSaveState('saved');
      // Fade the "saved" indicator after a moment.
      window.setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('error');
    }
  };

  return (
    <div style={{ background: '#fffbe6', padding: 8, borderRadius: 4 }}>
      <div style={{ marginBottom: 4, fontWeight: 500 }}>
        <LockOutlined /> Internal Notes{' '}
        <span style={{ color: '#888', fontWeight: 400 }}>(not shown to customer)</span>
        {saveState === 'saving' && (
          <span style={{ marginLeft: 8, color: '#888', fontWeight: 400 }}>
            <LoadingOutlined /> Saving…
          </span>
        )}
        {saveState === 'saved' && (
          <span style={{ marginLeft: 8, color: '#52c41a', fontWeight: 400 }}>
            <CheckCircleTwoTone twoToneColor="#52c41a" /> Saved
          </span>
        )}
        {saveState === 'error' && (
          <span style={{ marginLeft: 8, color: '#ff4d4f', fontWeight: 400 }}>Save failed</span>
        )}
      </div>
      <Input.TextArea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={handleBlur}
        rows={rows}
      />
    </div>
  );
};

export default InternalNotesField;
